import { blinkLikeToBinderPayloadAsync, ensureBlinkLikeFlatStringsForBinder } from '../regulaToBlinkResult'
import { crossCheckMrzCurp } from './cross-check-mrz-curp'

type CurpDerived = { birth?: string; sex?: string | null }

function deriveFromCurp(curp: string): CurpDerived {
  if (!curp || curp.length < 11) return {}
  const yy = curp.slice(4, 6)
  const mm = curp.slice(6, 8)
  const dd = curp.slice(8, 10)
  const y = +yy
  const cc = y <= 30 ? '20' : '19'
  const sex = curp[10] === 'H' ? 'H' : curp[10] === 'M' ? 'M' : null
  if (!/^\d{2}$/.test(mm) || !/^\d{2}$/.test(dd)) return { sex }
  return { birth: `${dd}/${mm}/${cc}${yy}`, sex }
}

function parseRegistrationYear(raw: string): number | null {
  const s = String(raw || '').trim()
  if (!s) return null

  const four = s.match(/\b(19|20)\d{2}\b/)
  if (four) {
    const y = parseInt(four[0], 10)
    return Number.isFinite(y) ? y : null
  }

  const digits = s.replace(/\D/g, '')
  if (digits.length === 4) {
    const y = parseInt(digits, 10)
    return Number.isFinite(y) && y >= 1900 && y <= 2099 ? y : null
  }
  if (digits.length === 2) {
    const yy = parseInt(digits, 10)
    if (!Number.isFinite(yy)) return null
    return yy <= 30 ? 2000 + yy : 1900 + yy
  }

  return null
}

interface IParams {
  idReadingData: any
}

/**
 * INE electoral section is numeric (typically 4 digits).
 * Front OCR may misread it as letters; MRZ may yield "unknown".
 * Prefer any candidate that is actually numbers (MRZ first when both are numeric).
 */
function pickNumericElectoralSection(...candidates: string[]): string {
  const cleaned = candidates.map((c) => String(c ?? '').trim()).filter(Boolean)

  const asFourDigits = (c: string): string | null => {
    if (/^\d{4}$/.test(c)) return c
    const digits = c.replace(/\D/g, '')
    if (/^\d{4}$/.test(digits)) return digits
    const m = c.match(/\d{4}/)
    return m ? m[0] : null
  }

  for (const c of cleaned) {
    const four = asFourDigits(c)
    if (four) return four
  }
  for (const c of cleaned) {
    if (/^\d+$/.test(c)) return c
  }
  return ''
}

function parseMxAddressParts(lines: string[]) {
  const joined = lines.filter(Boolean).join('\n').trim()
  const all = joined.replace(/\s+/g, ' ').trim()
  const mCp = all.match(/\b(\d{5})\b/)
  const cp = mCp ? mCp[1] : ''

  // Heuristics:
  // - calle: first line
  // - colonia: second line (without CP)
  // - municipio/entidad: often last line "MUNICIPIO, ESTADO"
  const calle = (lines[0] || '').trim()

  let colonia = (lines[1] || '').trim()
  if (cp) colonia = colonia.replace(cp, '').trim()

  const last = (lines[lines.length - 1] || '').trim()
  let municipio = ''
  let entidad = ''
  const parts = last
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
  if (parts.length >= 2) {
    municipio = parts[0]
    entidad = parts.slice(1).join(', ')
  } else if (parts.length === 1) {
    // sometimes last line is just state; municipality may be in the line before
    entidad = parts[0]
    const prev = (lines[lines.length - 2] || '').trim()
    if (prev && prev !== calle) municipio = prev.replace(cp, '').trim()
  }

  return { joined, calle, colonia, codigoPostal: cp, municipio, entidad }
}

function expandTwoDigitYear(yy: number, kind: 'birth' | 'expiry' | 'general' = 'general'): number {
  if (kind === 'expiry') return 2000 + yy
  if (kind === 'birth') {
    const nowYY = new Date().getFullYear() % 100
    return (yy > nowYY ? 1900 : 2000) + yy
  }
  return yy < 50 ? 2000 + yy : 1900 + yy
}

function mrzYYMMDDToBlinkDate(s: string, kind: 'birth' | 'expiry' = 'birth') {
  const raw = String(s || '').trim()
  if (!/^\d{6}$/.test(raw)) return null
  const yy = Number(raw.slice(0, 2))
  const mm = Number(raw.slice(2, 4))
  const dd = Number(raw.slice(4, 6))
  if (!yy || mm < 1 || mm > 12 || dd < 1 || dd > 31) return null
  const year = expandTwoDigitYear(yy, kind)
  return {
    day: dd,
    month: mm,
    year,
    originalString: {
      latin: raw,
      arabic: '',
      cyrillic: '',
      greek: '',
      latinLocation: undefined,
    },
    successfullyParsed: true,
  }
}

async function getBinderResult({ idReadingData }: IParams) {
  const blinkLike = { result: getDefaultBlinkResult() as any } as any

  // --- Map OUR reader output -> Blink-like result keys ---
  // `ocrResults` comes from the segmentation+OCR model (front side).
  const ocrRows: any[] = Array.isArray(idReadingData?.ocrResults) ? idReadingData.ocrResults : []

  const byField = new Map<string, string>()
  for (const r of ocrRows) {
    const field = String(r?.field ?? r?.label ?? '').trim()
    const text = String(r?.text ?? '').trim()
    if (!field || !text) continue
    // keep first non-empty (model usually outputs one per class)
    if (!byField.has(field)) byField.set(field, text)
  }

  // --- Map segmentation/OCR fields (front side) into Blink-like keys ---
  const firstName = byField.get('name') || ''
  const lastName = byField.get('last_name') || ''
  const secondSurname = byField.get('second_last_name') || ''
  const completeName = byField.get('complete_name') || ''
  const addressFromWhole = (byField.get('complete_address') || '').trim()
  const addressLines = [byField.get('address_1'), byField.get('address_2'), byField.get('address_3')]
    .map((line) => String(line || '').trim())
    .filter(Boolean)
  const addressFromLines = addressLines.join('\n').trim()
  const address =
    addressFromLines ||
    addressFromWhole
      .split(/\s*\/\s*|\n+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .join('\n')

  console.log('[id-ocr] Domicilio — binder:')
  console.log('  Línea 1 (address_1):', byField.get('address_1') || '(vacío)')
  console.log('  Línea 2 (address_2):', byField.get('address_2') || '(vacío)')
  console.log('  Línea 3 (address_3):', byField.get('address_3') || '(vacío)')
  console.log('  complete_address:', addressFromWhole || '(vacío)')
  console.log('  → address enviado al binder (\\n):', address || '(vacío)')
  const voterKey = byField.get('cve') || '' // INE "Clave de elector"
  const section = byField.get('section') || ''
  const issueNumber = byField.get('issue') || ''
  const curp = byField.get('curp') || ''
  const curpDerived = deriveFromCurp(curp)
  const gender = curpDerived.sex || byField.get('gender') || ''

  const birthdate = curpDerived.birth || byField.get('birthdate') || ''
  const issueDate = byField.get('issue_date') || ''
  const expiryDate = byField.get('expiry_date') || ''
  const registerDate = byField.get('register_date') || ''

  if (firstName) blinkLike.result.firstName = toBlinkTextValue(firstName)
  if (lastName) blinkLike.result.lastName = toBlinkTextValue(lastName)
  if (secondSurname) blinkLike.result.secondSurname = toBlinkTextValue(secondSurname)

  if (completeName) {
    blinkLike.result.fullName = toBlinkTextValue(completeName)
  } else if (firstName || lastName || secondSurname) {
    const composed = [firstName, lastName, secondSurname].filter(Boolean).join(' ')
    if (composed) blinkLike.result.fullName = toBlinkTextValue(composed)
  }

  if (address) blinkLike.result.address = toBlinkTextValue(address)
  if (voterKey) {
    blinkLike.result.voterKey = toBlinkTextValue(voterKey)
    // Also keep it in "documentAdditionalNumber" for some legacy flows.
    blinkLike.result.documentAdditionalNumber = toBlinkTextValue(voterKey)
  }
  // Section is numeric — ignore letter OCR noise here; MRZ merge below may fill it.
  {
    const sectionDigits = pickNumericElectoralSection(section)
    if (sectionDigits) blinkLike.result.section = toBlinkTextValue(sectionDigits)
  }
  if (issueNumber) {
    blinkLike.result.issueNumber = toBlinkTextValue(issueNumber)
  }
  if (curp) {
    blinkLike.result.personalIdNumber = toBlinkTextValue(curp)
    // Many backends/forms treat CURP as optionalData too.
    if (!blinkLike.result.optionalData?.latin) {
      blinkLike.result.optionalData = toBlinkTextValue(curp)
    }
  }
  if (gender) blinkLike.result.sex = toBlinkTextValue(gender)

  if (birthdate) blinkLike.result.dateOfBirth = toBlinkDateValue(birthdate)
  if (issueDate) blinkLike.result.dateOfIssue = toBlinkDateValue(issueDate)
  if (expiryDate) blinkLike.result.dateOfExpiry = toBlinkDateValue(expiryDate, 'expiry')
  if (registerDate) blinkLike.result.dateOfRegistration = toRegistrationYearValue(registerDate)

  const regYear = parseRegistrationYear(registerDate)
  const issueNum = parseInt(String(issueNumber).replace(/\D/g, ''), 10)
  if (regYear != null && Number.isFinite(issueNum)) {
    blinkLike.result.documentOptionalAdditionalNumber = toBlinkTextValue(
      `${regYear}${String(issueNum).padStart(2, '0')}`,
    )
  }

  // --- Map MRZ parse output (back side) as fallback/enrichment ---
  const mrz = idReadingData?.mrzData ?? null

  console.log('----------------', mrz)

  if (mrz && typeof mrz === 'object') {
    const givenNames = String((mrz as any).givenNames || '').trim()
    const surname1 = String((mrz as any).surname1 || '').trim()
    const surname2 = String((mrz as any).surname2 || '').trim()
    const nationality = String((mrz as any).nationality || '').trim()
    const genderMrz = String((mrz as any).gender || '').trim()
    const cic = String((mrz as any).cic || '').trim()
    const electoralSection = String((mrz as any).electoralSection || '').trim()
    const dobMrz = String((mrz as any).dateOfBirth || '').trim()
    const expMrz = String((mrz as any).expiryDate || '').trim()

    const rawLines: string[] = Array.isArray((mrz as any).rawProcessed) ? (mrz as any).rawProcessed : []
    const rawMrzString = rawLines.filter(Boolean).join('\n').trim()
    // Prefer numeric section from MRZ or front OCR (never letters like misread OCR).
    const sectionOcr = String(section || '').trim()
    const sectionMrz = String(electoralSection || '').trim()
    const sectionPreferred = pickNumericElectoralSection(sectionMrz, sectionOcr)
    const sanitizedOpt1 = sectionPreferred

    // flowy-cub3-gizmo-web binder maps internal `cic` from payload.mrz.documentNumber /
    // payload.mrzResult.documentNumber (NOT from a root-level `cic` key).
    const mrzForBinder: Record<string, unknown> = {
      ...(typeof blinkLike.result.mrz === 'object' && blinkLike.result.mrz ? (blinkLike.result.mrz as object) : {}),
      ...(cic ? { documentNumber: cic } : {}),
      ...(rawMrzString ? { rawMRZString: rawMrzString } : {}),
      ...(sanitizedOpt1 ? { sanitizedOpt1 } : {}),
    }
    const mrzResultForBinder: Record<string, unknown> = {
      ...(typeof blinkLike.result.mrzResult === 'object' && blinkLike.result.mrzResult
        ? (blinkLike.result.mrzResult as object)
        : {}),
      ...(cic ? { documentNumber: cic } : {}),
      ...(rawMrzString ? { rawMRZString: rawMrzString, mrzText: rawMrzString } : {}),
      ...(sanitizedOpt1 ? { sanitizedOpt1 } : {}),
    }
    if (cic || rawMrzString || sanitizedOpt1) {
      blinkLike.result.mrz = mrzForBinder as any
      blinkLike.result.mrzResult = mrzResultForBinder as any
    }

    if (rawMrzString) blinkLike.result.mrzString = toBlinkTextValue(rawMrzString)

    /**
     * MRZ > OCR for most fields when MRZ has a value.
     * SECCIÓN is special: always prefer the numeric value between front OCR and MRZ.
     */
    if (givenNames) blinkLike.result.firstName = toBlinkTextValue(givenNames)
    if (surname1) blinkLike.result.lastName = toBlinkTextValue(surname1)
    if (surname2) blinkLike.result.secondSurname = toBlinkTextValue(surname2)
    if (genderMrz) blinkLike.result.sex = toBlinkTextValue(genderMrz)
    if (nationality) blinkLike.result.nationality = toBlinkTextValue(nationality)
    if (sectionPreferred) {
      blinkLike.result.section = toBlinkTextValue(sectionPreferred)
    }

    const dob = mrzYYMMDDToBlinkDate(dobMrz, 'birth')
    if (dob) blinkLike.result.dateOfBirth = dob
    const exp = mrzYYMMDDToBlinkDate(expMrz, 'expiry')
    if (exp) blinkLike.result.dateOfExpiry = exp

    // Rebuild full name from MRZ parts for consistency.
    const full = [givenNames, surname1, surname2].filter(Boolean).join(' ').trim()
    if (full) blinkLike.result.fullName = toBlinkTextValue(full)
  }

  const crossCheck = crossCheckMrzCurp(
    curp,
    mrz && typeof mrz === 'object' && (mrz as any).dateOfBirth ? (mrz as any) : null,
  )
  blinkLike.result.dataMatchResult = {
    state: crossCheck.state,
    states: crossCheck.states,
  }

  ensureBlinkLikeFlatStringsForBinder(blinkLike)
  const payload = await blinkLikeToBinderPayloadAsync(blinkLike)

  // --- Extra aliases for Flowy forms (flat strings) ---
  // Some forms bind directly to these keys instead of Blink's nested structures.
  try {
    ;(payload as any).dataMatchResult = crossCheck
    ;(payload as any).curpMrzCrossCheck = crossCheck

    const addrLines = addressLines.length
      ? addressLines
      : addressFromWhole
          .split(/\s*\/\s*|\n+/)
          .map((s) => s.trim())
          .filter(Boolean)
    const addrJoined = (address || '').trim()
    const parts = parseMxAddressParts(addrLines.length ? addrLines : addrJoined.split(/\n+/))
    ;(payload as any).direccionCompleta = addrJoined || parts.joined
    ;(payload as any).calle = parts.calle
    ;(payload as any).colonia = parts.colonia
    ;(payload as any).codigoPostal = parts.codigoPostal
    ;(payload as any).municipio = parts.municipio
    ;(payload as any).entidad = parts.entidad

    // Force the binder's `electoralSection` source of truth.
    // The core computes `electoralSection` from `payload.mrz.sanitizedOpt1.slice(0,4)`.
    // Prefer whichever of MRZ / front OCR is numeric.
    const mrzSection = mrz && typeof mrz === 'object' ? String((mrz as any).electoralSection || '').trim() : ''
    const ocrSection = String(byField.get('section') || '').trim()
    const preferredSection = pickNumericElectoralSection(mrzSection, ocrSection)
    if (preferredSection) {
      ;(payload as any).section = preferredSection
      if ((payload as any).result && typeof (payload as any).result === 'object') {
        ;(payload as any).result.section = {
          latin: preferredSection,
          arabic: '',
          cyrillic: '',
          greek: '',
        }
      }
      ;(payload as any).mrz = {
        ...(payload as any).mrz,
        sanitizedOpt1: preferredSection,
      }
      ;(payload as any).mrzResult = {
        ...(payload as any).mrzResult,
        sanitizedOpt1: preferredSection,
      }
      // Defensive fallback: if core falls back to documentNumber.latin.slice(0,4),
      // make sure it also starts with the correct section.
      ;(payload as any).documentNumber = {
        latin: preferredSection,
        arabic: '',
        cyrillic: '',
        greek: '',
        latinLocation: undefined,
      }
    }

    const rawLines: string[] =
      mrz && typeof mrz === 'object' && Array.isArray((mrz as any).rawProcessed) ? (mrz as any).rawProcessed : []
    const mrzRaw = rawLines.filter(Boolean).join('\n').trim()
    const cicMrz = mrz && typeof mrz === 'object' ? String((mrz as any).cic || '').trim() : ''
    if (cicMrz) {
      // Forms often bind to `cic` (INE MRZ); flat aliases match codigoPostal/mrzString pattern.
      ;(payload as any).cic = cicMrz
      ;(payload as any).cicMrz = cicMrz
      // Binder core reads CIC only from mrz.documentNumber / mrzResult.documentNumber.
      ;(payload as any).mrz = {
        ...(payload as any).mrz,
        documentNumber: cicMrz,
      }
      ;(payload as any).mrzResult = {
        ...(payload as any).mrzResult,
        documentNumber: cicMrz,
      }
    }
    if (mrzRaw) {
      // Provide multiple aliases because different forms bind differently.
      ;(payload as any).mrzString = mrzRaw // string alias
      ;(payload as any).mrzRawMRZString = mrzRaw
      ;(payload as any).rawMRZString = mrzRaw
      ;(payload as any).mrz = { ...(payload as any).mrz, rawMRZString: mrzRaw }
      ;(payload as any).mrzResult = {
        ...(payload as any).mrzResult,
        rawMRZString: mrzRaw,
      }
      // Also provide Blink-text style alias in case the binder expects {latin: ...}
      ;(payload as any).mrzStringText = {
        latin: mrzRaw,
        arabic: '',
        cyrillic: '',
        greek: '',
        latinLocation: undefined,
      }
    }
  } catch {
    // best effort only
  }

  try {
    if (idReadingData?.frontImageDataUrl) {
      const frontImageData = await base64ToImageData(String(idReadingData.frontImageDataUrl))

      // I want to set the frontImageData
      // to the path fullDocumentFrontImage.rawImage
      ;(payload as any).fullDocumentFrontImage = {
        rawImage: frontImageData,
        encodedImage: [],
      }
    } else if (idReadingData?.frontImage) {
      const frontImageData = imageBitmapToImageData(idReadingData.frontImage as ImageBitmap)
      ;(payload as any).fullDocumentFrontImage = {
        rawImage: frontImageData,
        encodedImage: [],
      }
    }
  } catch {}

  try {
    if (idReadingData?.backImageDataUrl) {
      const backImageData = await base64ToImageData(String(idReadingData.backImageDataUrl))
      // I want to set the backImageData
      // to the path fullDocumentBackImage.rawImage
      ;(payload as any).fullDocumentBackImage = {
        rawImage: backImageData,
        encodedImage: [],
      }
    }
    if (idReadingData?.backImage) {
      const backImageData = imageBitmapToImageData(idReadingData.backImage as ImageBitmap)
      ;(payload as any).fullDocumentBackImage = {
        rawImage: backImageData,
        encodedImage: [],
      }
    }
  } catch {}

  return payload
}

/** Objeto vacío de texto Blink (latin, arabic, cyrillic, greek, latinLocation). */
function emptyBlinkText() {
  return {
    latin: '',
    arabic: '',
    cyrillic: '',
    greek: '',
    latinLocation: undefined,
  }
}

/** Objeto vacío de fecha Blink. */
function emptyBlinkDate() {
  return {
    day: 0,
    month: 0,
    year: 0,
    originalString: {},
    successfullyParsed: false,
  }
}

/** Estructura completa de result como en BlinkID (imagen de ejemplo). Todos los campos con su forma por defecto. */
function getDefaultBlinkResult() {
  const text = emptyBlinkText
  const date = emptyBlinkDate
  return {
    firstName: text(),
    lastName: text(),
    fathersName: text(),
    mothersName: text(),
    fullName: text(),
    sex: text(),
    dateOfBirth: date(),
    nationality: text(),
    address: text(),
    placeOfBirth: text(),
    bloodType: text(),
    maritalStatus: text(),
    profession: text(),
    race: text(),
    religion: text(),
    employer: text(),
    additionalAddressInformation: text(),
    additionalNameInformation: text(),
    additionalOptionalAddressInformation: text(),
    documentNumber: text(),
    documentAdditionalNumber: text(),
    documentOptionalAdditionalNumber: text(),
    documentSubtype: text(),
    dateOfIssue: date(),
    dateOfExpiry: date(),
    dateOfExpiryPermanent: false,
    issuingAuthority: text(),
    classInfo: {
      country: 0,
      region: 0,
      documentType: 0,
      countryName: '',
      isoNumericCountryCode: '',
    },
    recognitionMode: 4,
    scanningFirstSideDone: true,
    state: 2,
    manufacturingYear: text(),
    mrz: {
      alienNumber: {},
      applicationReceiptNumber: {},
      dateOfBirth: {},
      dateOfExpiry: {},
      documentCode: {},
    },
    backAdditionalProcessingInfo: {
      missingMandatoryFields: [],
      invalidCharacterFields: [],
      extraPresentFields: [],
      imageExtractionFailures: [],
    },
    frontAdditionalProcessingInfo: {
      missingMandatoryFields: [],
      invalidCharacterFields: [],
      extraPresentFields: [],
      imageExtractionFailures: [],
    },
    backImageAnalysisResult: {
      blurDetected: false,
      glareDetected: false,
      cardOrientation: 0,
      documentImageColorStatus: 2,
      documentImageMoireStatus: 0,
    },
    frontImageAnalysisResult: {
      blurDetected: false,
      glareDetected: false,
      cardOrientation: 0,
      documentImageColorStatus: 2,
      documentImageMoireStatus: 0,
    },
    backCameraFrame: { frame: null, orientation: 0 },
    frontCameraFrame: { frame: null, orientation: 0 },
    backProcessingStatus: 0,
    frontProcessingStatus: 0,
    barcode: {
      additionalNameInformation: {},
      address: {},
      addressDetailedInfo: {},
      barcodeData: {},
      dateOfBirth: {},
    },
    barcodeCameraFrame: { frame: null, orientation: 0 },
    barcodeStepUsed: false,
    dataMatchResult: { state: 0, states: [] },
    dependentsInfo: [],
    driverLicenseDetailedInfo: {
      conditions: [],
      endorsements: [],
      restrictions: [],
      vehicleClass: [],
      vehicleClassesInfo: [],
    },
    eligibilityCategory: text(),
    localizedName: text(),
    personalIdNumber: text(),
    residencePermitType: text(),
    residentialStatus: text(),
    specificDocumentValidity: text(),
    sponsor: text(),
    vehicleOwner: text(),
    vehicleType: text(),
    visaType: text(),
    documentClass: text(),
    issuingState: text(),
    issuingStateName: text(),
    nationalityCode: text(),
    section: text(),
    voterKey: text(),
    bookletNumber: text(),
    issueNumber: text(), // número de emisión
    dateOfRegistration: date(),
    url: text(),
    secondSurname: text(),
    mrzString: text(),
    dlClass: text(),
    dlEndorsement: text(),
    dlRestriction: text(),
    optionalData: text(),
    height: text(),
    weight: text(),
    eyeColor: text(),
    hairColor: text(),
    faceImage: { rawImage: null, encodedImage: [] },
    fullDocumentBackImage: { rawImage: null, encodedImage: [] },
    fullDocumentFrontImage: { rawImage: null, encodedImage: [] },
    signatureImage: { rawImage: null, encodedImage: [] },
  }
}

function toBlinkTextValue(str: string) {
  const s = str != null && str !== '' ? String(str).trim() : ''
  return {
    latin: s,
    arabic: '',
    cyrillic: '',
    greek: '',
    latinLocation: undefined,
  }
}

function toRegistrationYearValue(str: string) {
  const originalString = str != null && str !== '' ? String(str).trim() : ''
  const year = parseRegistrationYear(originalString)
  if (year != null) {
    return {
      day: 0,
      month: 0,
      year,
      originalString,
      successfullyParsed: true,
    }
  }
  return {
    day: 0,
    month: 0,
    year: 0,
    originalString,
    successfullyParsed: false,
  }
}

function toBlinkDateValue(str: string, kind: 'birth' | 'expiry' | 'general' = 'general') {
  const originalString = str != null && str !== '' ? String(str).trim() : ''
  const parsed = parseDateString(originalString, kind)
  if (parsed && Number.isFinite(parsed.year)) {
    return {
      day: parsed.day,
      month: parsed.month,
      year: parsed.year,
      originalString,
      successfullyParsed: true,
    }
  }
  return {
    day: 0,
    month: 0,
    year: 0,
    originalString,
    successfullyParsed: false,
  }
}

function parseDateString(str: string, kind: 'birth' | 'expiry' | 'general' = 'general') {
  if (!str || typeof str !== 'string') return null
  const s = str.trim()
  if (!s) return null

  // DD.MM.YYYY or DD/MM/YYYY
  let m = s.match(/^(\d{1,2})[./](\d{1,2})[./](\d{2,4})$/)
  if (m) {
    const day = parseInt(m[1], 10)
    const month = parseInt(m[2], 10)
    let year = parseInt(m[3], 10)
    if (year < 100) year = expandTwoDigitYear(year, kind)
    return { day, month, year }
  }

  // YYYY-MM-DD
  m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/)
  if (m) {
    const year = parseInt(m[1], 10)
    const month = parseInt(m[2], 10)
    const day = parseInt(m[3], 10)
    return { day, month, year }
  }

  // Compact forms: DDMMYYYY or DDMMYY
  m = s.match(/^(\d{8})$/)
  if (m) {
    const n = m[1]
    const day = parseInt(n.slice(0, 2), 10)
    const month = parseInt(n.slice(2, 4), 10)
    const year = parseInt(n.slice(4, 8), 10)
    return { day, month, year }
  }
  m = s.match(/^(\d{6})$/)
  if (m) {
    const n = m[1]
    const day = parseInt(n.slice(0, 2), 10)
    const month = parseInt(n.slice(2, 4), 10)
    const year = expandTwoDigitYear(parseInt(n.slice(4, 6), 10), kind)
    return { day, month, year }
  }

  // Year only (2 or 4 digits) — e.g. registration year
  m = s.match(/^(\d{4})$/)
  if (m) {
    const year = parseInt(m[1], 10)
    return Number.isFinite(year) ? { day: 0, month: 0, year } : null
  }
  m = s.match(/^(\d{2})$/)
  if (m) {
    const year = expandTwoDigitYear(parseInt(m[1], 10), kind)
    return Number.isFinite(year) ? { day: 0, month: 0, year } : null
  }

  // Year embedded in OCR noise
  m = s.match(/\b(19|20)\d{2}\b/)
  if (m) {
    const year = parseInt(m[0], 10)
    return Number.isFinite(year) ? { day: 0, month: 0, year } : null
  }

  return null
}

/**
 * Converts a Base64 string to a Canvas ImageData object.
 * @param {string} base64 - The full base64 string (including data:image/...)
 * @returns {Promise<ImageData>}
 */
async function base64ToImageData(base64: string): Promise<ImageData> {
  return new Promise((resolve, reject) => {
    const img = new Image()

    img.onload = () => {
      // 1. Create an off-screen canvas
      const canvas = document.createElement('canvas')
      canvas.width = img.width
      canvas.height = img.height
      const ctx = canvas.getContext('2d')

      // 2. Draw the image onto the canvas
      ctx!.drawImage(img, 0, 0)

      // 3. Extract the ImageData
      const imageData = ctx!.getImageData(0, 0, img.width, img.height)
      resolve(imageData)
    }

    img.onerror = () => reject(new Error('Failed to load image from Base64'))

    // Set the source to the base64 string
    img.src = base64
  })
}

function imageBitmapToImageData(imageBitmap: ImageBitmap): ImageData {
  // 1. Create an offscreen canvas with the same dimensions
  const canvas = new OffscreenCanvas(imageBitmap.width, imageBitmap.height)
  const ctx = canvas.getContext('2d') as OffscreenCanvasRenderingContext2D | null

  if (!ctx) {
    throw new Error('Could not get 2D context from canvas')
  }

  // 2. Draw the ImageBitmap onto the canvas
  ctx.drawImage(imageBitmap, 0, 0)

  // 3. Extract and return the ImageData
  return ctx.getImageData(0, 0, imageBitmap.width, imageBitmap.height)
}

export default getBinderResult
